import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

type TooltipProps = {
  text: string;
  align: "top" | "bottom";
  sideAlign?: "right" | "left";
  disabled?: boolean;
  children?: React.ReactNode;
};

const Tooltip = ({
  text,
  disabled,
  children,
  align = "top",
  sideAlign = "right",
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const wrap = wrapperRef.current;
    const tip = tooltipRef.current;
    if (!wrap || !visible || disabled) return;

    const cr = wrap.getBoundingClientRect();
    const gap = 10;
    let top = 0;
    let left = 0;
    const pad = 8;

    if (tip) {
      const tr = tip.getBoundingClientRect();
      if (align === "top") {
        top = cr.top - tr.height - gap;
        left = sideAlign === "right" ? cr.left : cr.right - tr.width;
      } else {
        top = cr.bottom + gap;
        left = sideAlign === "right" ? cr.left : cr.right - tr.width;
      }
      left = Math.max(pad, Math.min(left, window.innerWidth - tr.width - pad));
      top = Math.max(pad, Math.min(top, window.innerHeight - tr.height - pad));
    } else {
      if (align === "top") {
        top = cr.top - 48;
        left = sideAlign === "right" ? cr.left : cr.right - 180;
      } else {
        top = cr.bottom + gap;
        left = sideAlign === "right" ? cr.left : cr.right - 180;
      }
    }
    setPos({ top, left });
  }, [visible, disabled, align, sideAlign]);

  useLayoutEffect(() => {
    if (!visible || disabled) return;

    updatePosition();
    const tip = tooltipRef.current;
    const ro =
      typeof ResizeObserver !== "undefined" && tip
        ? new ResizeObserver(() => updatePosition())
        : null;
    if (tip && ro) ro.observe(tip);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, disabled, updatePosition, text]);

  const tooltipEl =
    visible &&
    !disabled &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${styles.tooltipLayer} ${styles[align]} ${styles[sideAlign]}`}
        style={{ top: pos.top, left: pos.left }}
        role="tooltip"
      >
        <div className={styles.text}>{text}</div>
        <p className={styles.arrow}></p>
      </div>,
      document.body
    );

  return (
    <div
      ref={wrapperRef}
      className={styles.container}
      onMouseEnter={() => !disabled && setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {tooltipEl}
    </div>
  );
};

export default Tooltip;
